import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onBeforeMount } from "vue";
import { BookingService } from "@/services/BookingService";
import { useToast } from "@/services/useToast";
import router from "@/router";
import { differenceInCalendarDays, parseISO } from "date-fns";
import AdminUpdateBooking from "@/components/Bookings/admin/AdminUpdateBooking.vue";
import BookingVersionHistory from "@/components/Bookings/admin/BookingVersionHistory.vue";
import BookingConfirmationEmail from "@/components/Bookings/admin/BookingConfirmationEmail.vue";
import BusinessProcessActivityTimeline from "@/components/activity/BusinessProcessActivityTimeline.vue";
import ActivityCreator from "@/components/activity/admin/ActivityCreator.vue";
import ResourceErrorState from "@/components/layout/ResourceErrorState.vue";
import { useResourceState } from "@/services/useResourceState";

type IfaValues = { ifaAmount: number | null; ifaCount: number | null };


export default /*#__PURE__*/_defineComponent({
  __name: 'AdminBookingDetailsView',
  setup(__props, { expose: __expose }) {
  __expose();

const booking = ref<any>({});
const bookingVersions = ref<any>([]);
const timeline = ref<InstanceType<
  typeof BusinessProcessActivityTimeline
> | null>(null);
const bookingsService = new BookingService("/admin/bookings");
const { showToast } = useToast();

const bookingId = router.currentRoute.value.params.id as string;
const { loading, error, runLoad, runRefresh } = useResourceState();

// Status change state
const selectedStatus = ref("");
const statusUpdating = ref(false);
const showCancelConfirm = ref(false);

const statusOptions = [
  { label: "Lefoglalva", value: "BOOKED" },
  { label: "Teljesítve", value: "COMPLETED" },
  { label: "Törölve", value: "CANCELLED" },
];

/**
 * Loaded through runLoad rather than a top-level await: an async setup() has no way to report
 * a failure, so a missing booking used to leave the view blank with nothing on screen to say so.
 */
const loadBooking = async () => {
  await fetchBooking();
  await fetchBookingVersions();
};

const fetchData = () => runLoad(loadBooking);
// Reloads after an edit made on this page: the booking stays on screen while it refreshes.
const refreshData = () => runRefresh(loadBooking);

onBeforeMount(fetchData);

const fetchBooking = async () => {
  booking.value = await bookingsService.get(bookingId);
  selectedStatus.value = booking.value.status;
  snapshotIfa();
};

const fetchBookingVersions = async () => {
  bookingVersions.value = await bookingsService.getVersions(bookingId);
};

const refreshTimeline = async () => {
  if (timeline.value) {
    timeline.value.fetchTimeline();
  }
};

const onStatusChangeClick = () => {
  if (selectedStatus.value === "CANCELLED") {
    showCancelConfirm.value = true;
  } else {
    performStatusChange();
  }
};

const confirmStatusChange = () => {
  showCancelConfirm.value = false;
  performStatusChange();
};

const performStatusChange = async () => {
  statusUpdating.value = true;
  try {
    await bookingsService.updateStatus(bookingId, selectedStatus.value);
    await refreshData();
    showToast("Státusz frissítve");
  } catch {
    showToast("Státusz frissítése sikertelen", "error");
  } finally {
    statusUpdating.value = false;
  }
};
// ---------- Per-item IFA editing ----------
// Edited in place on the current booking version: no new revision, bills stay attached.

const ifaSavingId = ref<number | null>(null);
const ifaOriginals = ref<Record<number, IfaValues>>({});

// v-model on a number field yields "" when cleared, which must reach the API as null.
const toNullableInt = (value: any): number | null => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const snapshotIfa = () => {
  const snapshot: Record<number, IfaValues> = {};
  (booking.value.items ?? []).forEach((item: any) => {
    snapshot[item.id] = {
      ifaAmount: toNullableInt(item.ifaAmount),
      ifaCount: toNullableInt(item.ifaCount),
    };
  });
  ifaOriginals.value = snapshot;
};

snapshotIfa();

const isIfaDirty = (item: any) => {
  const original = ifaOriginals.value[item.id];
  if (!original) return false;
  return (
    toNullableInt(item.ifaAmount) !== original.ifaAmount ||
    toNullableInt(item.ifaCount) !== original.ifaCount
  );
};

const saveItemIfa = async (item: any) => {
  if (ifaSavingId.value !== null) return;
  ifaSavingId.value = item.id;
  try {
    await bookingsService.updateItemIfa(
      booking.value.publicReference,
      item.id,
      {
        ifaAmount: toNullableInt(item.ifaAmount),
        ifaCount: toNullableInt(item.ifaCount),
      }
    );
    await fetchBooking();
    showToast("IFA mentve");
  } catch {
    showToast("IFA mentése sikertelen", "error");
  } finally {
    ifaSavingId.value = null;
  }
};

// ---------- UI helpers ----------

const formatDateTime = (timestamp?: number) => {
  if (!timestamp) return "-";
  return new Date(timestamp).toLocaleString("hu-HU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPrice = (value?: number) => {
  if (!value) return "-";
  return `${value.toLocaleString("hu-HU")} Ft`;
};

// ---------- Table config ----------

const itemHeaders = [
  { title: "Szállás", key: "accommodation" },
  { title: "Város", key: "city" },
  { title: "Termék", key: "product" },
  { title: "Érkezés", key: "startDate" },
  { title: "Távozás", key: "endDate" },
  { title: "Létszám", key: "numberOfPeople", align: "end" },
  {
    title: "Bekerülési ár/fő/éj",
    key: "grossPricePerPersonPerNight",
    align: "end",
  },
  { title: "Kiajánlott ár/fő/éj", key: "retailPrice", align: "end" },
  {
    title: "Kiajánlott bruttó ár/fő/éj",
    key: "outgoingGrossPerPersonPerNight",
    align: "end",
  },
  { title: "IFA (Ft)", key: "ifaAmount", align: "end", sortable: false },
  { title: "IFA db", key: "ifaCount", align: "end", sortable: false },
  { title: "", key: "ifaActions", align: "end", sortable: false },
  { title: "Számlák", key: "incomingBills" },
];

// ---------- Computed totals ----------

const totalOutgoingPrice = computed(() => {
  if (!booking.value.items) return 0;
  return booking.value.items.reduce((sum: number, item: any) => {
    const start = parseISO(item.startDate);
    const end = parseISO(item.endDate);
    const numberOfNights = differenceInCalendarDays(end, start);
    return sum + numberOfNights * item.numberOfPeople * item.retailPrice;
  }, 0);
});

const totalIncomingPrice = computed(() => {
  if (!booking.value.items) return 0;
  return booking.value.items.reduce((sum: number, item: any) => {
    const start = parseISO(item.startDate);
    const end = parseISO(item.endDate);
    const numberOfNights = differenceInCalendarDays(end, start);
    return (
      sum +
      numberOfNights * item.numberOfPeople * item.grossPricePerPersonPerNight
    );
  }, 0);
});

const formatPublicReference = (ref: string) => {
  if (!ref) return "";
  return ref.match(/.{1,4}/g)!.join("-");
};

const BILL_STATUS_LABELS: Record<string, string> = {
  DUE: "Esedékes",
  PAID: "Fizetve",
  CANCELLED: "Törölve",
  OVERDUE: "Lejárt",
};

const BILL_STATUS_COLORS: Record<string, string> = {
  DUE: "orange",
  PAID: "green",
  CANCELLED: "grey",
  OVERDUE: "red",
};

const billStatusLabel = (s: string) => BILL_STATUS_LABELS[s] ?? s;
const billStatusColor = (s: string) => BILL_STATUS_COLORS[s] ?? "grey";

const __returned__ = { booking, bookingVersions, timeline, bookingsService, showToast, bookingId, loading, error, runLoad, runRefresh, selectedStatus, statusUpdating, showCancelConfirm, statusOptions, loadBooking, fetchData, refreshData, fetchBooking, fetchBookingVersions, refreshTimeline, onStatusChangeClick, confirmStatusChange, performStatusChange, ifaSavingId, ifaOriginals, toNullableInt, snapshotIfa, isIfaDirty, saveItemIfa, formatDateTime, formatPrice, itemHeaders, totalOutgoingPrice, totalIncomingPrice, formatPublicReference, BILL_STATUS_LABELS, BILL_STATUS_COLORS, billStatusLabel, billStatusColor, ref, computed, onBeforeMount, get BookingService() { return BookingService }, get useToast() { return useToast }, get router() { return router }, get differenceInCalendarDays() { return differenceInCalendarDays }, get parseISO() { return parseISO }, AdminUpdateBooking, BookingVersionHistory, BookingConfirmationEmail, BusinessProcessActivityTimeline, ActivityCreator, ResourceErrorState, get useResourceState() { return useResourceState } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})